/**
 * CrmWorkspace — the CRM (Leads / Opportunities / Proposals + Fee Models) as an in-app WORKSPACE,
 * like Billing/Pulse/Tracker. Docks into the content area beside SiteNav (tray-aware via the
 * `.aj-crm-ws` rule in outline.css) and shows when active; the nav stays usable and "Projects"
 * returns to the outline.
 *
 * Open: `open-crm` CustomEvent (detail { tool:'leads'|'opportunities'|'proposals'|'fee-models' }) —
 * dispatched by the SiteNav CRM item. Close: `close-crm` (SiteNav Projects + the other workspaces).
 *
 * State (active tab) persists to sessionStorage (per-tab live) + localStorage (shared snapshot) so a
 * refresh restores the workspace where you left it instead of dropping back to the outline.
 *
 * Data layer: React-on-Airtable. Views call the CRM service files (services/{leads,opportunities,
 * proposals,interactions,feeModels}.js). This file owns only the shell; tab views live in components/crm/.
 */

import { useEffect, useState } from 'react'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import CrmTodayView from './crm/CrmTodayView.jsx'
import CrmLeadsView from './crm/CrmLeadsView.jsx'
import CrmOpportunitiesView from './crm/CrmOpportunitiesView.jsx'
import CrmFeeModelsView from './crm/CrmFeeModelsView.jsx'
import CrmReferenceLibraryView from './crm/CrmReferenceLibraryView.jsx'
import CrmProposalsView from './crm/CrmProposalsView.jsx'
import CrmProspectingWidget from './crm/CrmProspectingWidget.jsx'

const STORE_KEY = 'aj:crm:ws'
// 'today' (Phase 2 of the always-on engine work) is the new default landing tab — the "someone
// manning the shop opens this every morning" surface, merging Leads + Opportunities next-steps.
const TABS = ['today', 'leads', 'opportunities', 'proposals']   // 'fee-models' appended for admins below
const TAB_LABEL = { today: 'Today', leads: 'Leads', opportunities: 'Opportunities', proposals: 'Proposals', 'fee-models': 'Fee Models', references: 'References' }

// Per-tab live state (sessionStorage) + shared last-session snapshot (localStorage), matching
// BillingWorkspace: read sessionStorage first, fall back to the shared snapshot; an explicit ws in
// the URL wins and suppresses the storage fallback.
function readStored() {
  try {
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    if (ws === 'crm') return { active: true, tool: params.get('wsTool') || 'today' }
    if (ws !== null) return null
    // A peek/split iframe must never auto-restore a workspace from storage shared with the
    // parent tab — see ScheduleIntelWorkspace.jsx's own comment on this same check.
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch (e) { return null }
}

export default function CrmWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [tool, setTool] = useState((stored && stored.tool) || 'today')
  const [seatReady, setSeatReady] = useState(isSeatLoaded())

  // open/close via events
  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      if (d.tool) setTool(d.tool)
      setActive(true)
    }
    function onClose() { setActive(false) }
    window.addEventListener('open-crm', onOpen)
    window.addEventListener('close-crm', onClose)
    return () => {
      window.removeEventListener('open-crm', onOpen)
      window.removeEventListener('close-crm', onClose)
    }
  }, [])

  // Persist the workspace state so a refresh restores it.
  useEffect(() => {
    try {
      if (active) {
        const json = JSON.stringify({ active: true, tool })
        sessionStorage.setItem(STORE_KEY, json)
        localStorage.setItem(STORE_KEY, json)
      } else {
        sessionStorage.removeItem(STORE_KEY)
        localStorage.removeItem(STORE_KEY)
      }
    } catch (e) { /* ignore */ }
  }, [active, tool])

  // Reflect the active workspace in the URL (deep-link + refresh-restore).
  useEffect(() => {
    const url = new URL(window.location)
    if (active) {
      url.searchParams.set('ws', 'crm')
      url.searchParams.set('wsTool', tool)
      document.title = 'Alder James CRM'
    } else if (url.searchParams.get('ws') === 'crm') {
      url.searchParams.delete('ws')
      url.searchParams.delete('wsTool')
    }
    window.history.replaceState(null, '', url)
  }, [active, tool])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  // Seat data loads async at startup; on a refresh-restore the views can't gate until it's ready.
  useEffect(() => {
    if (seatReady) return
    const id = setInterval(() => { if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) } }, 100)
    return () => clearInterval(id)
  }, [seatReady])

  // Don't strand a user without CRM access in an empty restored panel.
  useEffect(() => {
    if (active && seatReady && !(hasModule('crm') || hasModule('admin'))) setActive(false)
  }, [active, seatReady])

  if (!active) return null

  const restricted = isSeatLoaded() && !(hasModule('crm') || hasModule('admin'))
  const isAdmin = isSeatLoaded() && hasModule('admin')
  // Fee Models + References are both firm-wide curated datasets (not per-opportunity content),
  // admin-only like Fee Models already was — see docs/REFERENCES.md.
  const tabs = isAdmin ? [...TABS, 'fee-models', 'references'] : TABS
  // If a non-admin restored onto the fee-models tab, fall back to Today.
  const activeTool = tabs.includes(tool) ? tool : 'today'

  return (
    <div className="aj-crm-ws" style={S.panel}>
      <WorkspaceHeader
        title="CRM"
        titleExtra={!restricted && <CrmProspectingWidget />}
        tabs={!restricted && (
          <div style={workspaceTabsWrapStyle}>
            {tabs.map(t => (
              <button key={t} style={workspaceTabStyle(activeTool === t)} onClick={() => setTool(t)}>{TAB_LABEL[t]}</button>
            ))}
          </div>
        )}
      />
      {restricted ? (
        <div style={S.restricted}>You don't have a CRM seat. Contact your administrator.</div>
      ) : (
        <div style={S.content}>
          <CrmTab tool={activeTool} />
        </div>
      )}
    </div>
  )
}

// Tab content. Leads is live; the rest are filled in by later build steps.
function CrmTab({ tool }) {
  if (tool === 'today') return <CrmTodayView />
  if (tool === 'leads') return <CrmLeadsView />
  if (tool === 'opportunities') return <CrmOpportunitiesView />
  if (tool === 'proposals') return <CrmProposalsView />
  if (tool === 'fee-models') return <CrmFeeModelsView />
  if (tool === 'references') return <CrmReferenceLibraryView />
  return (
    <div style={S.placeholder}>
      <div style={S.placeholderTitle}>{TAB_LABEL[tool]}</div>
      <div style={S.placeholderSub}>Coming together.</div>
    </div>
  )
}

const S = {
  panel: { display: 'flex', flexDirection: 'column' },   // position/size/bg from .aj-crm-ws in outline.css
  content: { flex: 1, overflow: 'auto' },
  restricted: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' },
  placeholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 6 },
  placeholderTitle: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  placeholderSub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' },
}
